import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/auth/auth.service';
import { ExamService } from '../exam.service';
import { environment } from 'src/environments/environment';

interface ExamParams {
  guid: string;
  startDate: string;
  startTime: string;
  endTime: string;
}

interface ExamStatus {
  isValid: boolean;
  message: string;
  type: 'not-started' | 'expired' | 'valid' | 'invalid' | 'submitted';
}

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss']
})
export class UserDetailsComponent implements OnInit {
  loginForm!: FormGroup;
  examStatus: ExamStatus = {
    isValid: false,
    message: '',
    type: 'invalid'
  };
  currentYear = new Date().getFullYear();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private toastrService: ToastrService,
    private examService:ExamService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.validateExamSession();
  }

  private initializeForm(): void {
    this.loginForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      email: new FormControl('', [
        Validators.required,
        Validators.pattern('^[^/$><&*%^#!?\|~^,;:\'"\\\[\\]{}()+=`]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')
      ]),
      phoneNumber: new FormControl('', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(10),
        Validators.pattern('^[0-9]{10}$')
      ])
    });
  }

  private validateExamSession(): void {
    this.route.paramMap.subscribe(params => {
      const guid = params.get('sessionId');
      
      if (!guid) {
        this.handleExamStatus({
          isValid: false,
          message: 'Invalid exam link',
          type: 'invalid'
        });
        return;
      }

      this.examService.getExamDetails(guid).subscribe(
        (examParams:any) => {
          const status = this.checkExamTiming(examParams);
          this.handleExamStatus(status);
          this.loadExamState();
          if (status.isValid) {
            sessionStorage.setItem("sessionId", guid);
          }
        },
        (error:any) => {
          this.handleExamStatus({
            isValid: false,
            message: 'Failed to fetch exam details or invalid exam link',
            type: 'invalid'
          });
        }
      );
    });
  }

  private checkExamTiming(examParams: ExamParams): ExamStatus {
    const currentTime = new Date().getTime();
    const startTime = new Date(examParams.startDate.substring(0,10) + 'T' + this.utcToIst(examParams.startTime)).getTime();
    const expiryTime = new Date(examParams.startDate.substring(0,10)  + 'T' + this.utcToIst(examParams.endTime)).getTime();
  
    if (currentTime < startTime) {
      return {
        isValid: false,
        message: `Invalid Exam.`,
        type: 'invalid'
      };
    }
  
    if (currentTime > expiryTime) {
      return {
        isValid: false,
        message: 'This exam link has expired',
        type: 'expired'
      };
    }

    const isExamSubmitted = sessionStorage.getItem('isExamSubmitted');
    if(isExamSubmitted){
      return {
        isValid: false,
        message: 'Exam already submitted',
        type: 'submitted'
      };
    }
    
    return {
      isValid: true,
      message: '',
      type: 'valid'
    };
  }

  utcToIst(utcTime: string): string {
    const [hours, minutes] = utcTime.split(':').map(Number);
    let istHours = hours + 5;
    let istMinutes = minutes + 30;
    if (istMinutes >= 60) {
      istMinutes -= 60;
      istHours++;
    }
    if (istHours >= 24) {
      istHours -= 24;
    }
    const formattedHours = istHours.toString().padStart(2, '0');
    const formattedMinutes = istMinutes.toString().padStart(2, '0');
    return `${formattedHours}:${formattedMinutes}`;
  }
  
  private loadExamState() {
    const allowedToAccessExam = sessionStorage.getItem('allowedToAccessExam');
    if (allowedToAccessExam) {
      this.router.navigate(['/exam']);
    }
  }
  

  private handleExamStatus(status: ExamStatus): void {
    this.examStatus = status;
    
    if (!status.isValid) {
      this.toastrService.error(status.message, 'Error');
      this.loginForm.disable();
    }
  }

  onLogin() {
    if (!this.examStatus.isValid) {
      this.toastrService.error('This exam link is no longer valid', 'Error');
      return;
    }

    if (this.loginForm.invalid) {
      this.showValidationErrors();
      return;
    }

    this.examService.loginUser(this.loginForm.value).subscribe((res: any) => {
      const sessionBody = {
        examId: +(sessionStorage.getItem("sessionId") || '0'),
        userId: res.id,
        ipAddress: '',
        browserInfo: ''
      };
      this.examService.startSession(sessionBody).subscribe((res: any) => {
        sessionStorage.setItem('sessionCode', res.sessionCode);
        sessionStorage.setItem('userDetails', JSON.stringify(this.loginForm.value));
        sessionStorage.setItem("allowedToAccessExam", "true");
        this.toastrService.success('Login successful!', 'Success');
        this.router.navigate(['/exam']);
      });
    });
  }

  generateGUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private showValidationErrors() {
    const controls = this.loginForm.controls;

    if (controls['name'].hasError('required')) {
      this.toastrService.error('Name is required', 'Validation Error');
    }

    if (controls['email'].hasError('required')) {
      this.toastrService.error('Email is required', 'Validation Error');
    } else if (controls['email'].hasError('pattern')) {
      this.toastrService.error('Enter a valid email address', 'Validation Error');
    }

    if (controls['phoneNumber'].hasError('required')) {
      this.toastrService.error('Phone number is required', 'Validation Error');
    } else if (controls['phoneNumber'].hasError('pattern') || controls['phoneNumber'].hasError('minlength')) {
      this.toastrService.error('Phone number must be 10 digits', 'Validation Error');
    }
  }
}
